import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowSquareOut, Trash, MagnifyingGlass, Receipt, Image as ImageIcon, Truck } from '@phosphor-icons/react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useDataCache } from '@/contexts/DataCache';
import { useAuth } from '@/contexts/AuthContext';
import { HistoryDetailDialog } from '@/components/common/HistoryDetailDialog';
import { HistoryFilters } from '@/components/common/HistoryFilters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Persistencia local del borrador (carrito, motivo, flag Gerardo)
const DRAFT_KEY = 'pos_exits_draft_v1';
const _loadDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
};

export const Exits = () => {
  const { products, refreshProducts } = useDataCache();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState(() => _loadDraft()?.cart || []);
  const [reason, setReason] = useState(() => _loadDraft()?.reason || '');
  const [submitting, setSubmitting] = useState(false);
  const [isGerardoExit, setIsGerardoExit] = useState(() => _loadDraft()?.isGerardoExit || false);
  const [history, setHistory] = useState([]);
  const [pendingApproval, setPendingApproval] = useState(null);
  const [detail, setDetail] = useState({ open: false, record: null });
  const [allowNegative, setAllowNegative] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Al menos uno de los dos switches basta para permitir stock negativo
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/config/allow-negative-stock-exits`, { withCredentials: true }),
          axios.get(`${API}/config/allow-negative-stock`, { withCredentials: true }),
        ]);
        setAllowNegative(!!(r1.data?.enabled || r2.data?.enabled));
      } catch { /* ignore */ }
    })();
  }, []);

  const [hSearch, setHSearch] = useState('');
  const [hFrom, setHFrom] = useState('');
  const [hTo, setHTo] = useState('');
  const [hStatus, setHStatus] = useState('all');

  useEffect(() => { fetchHistory(); }, []);

  // Persistir borrador en cada cambio
  useEffect(() => {
    try {
      if (cart.length === 0 && !reason && !isGerardoExit) {
        localStorage.removeItem(DRAFT_KEY);
      } else {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ cart, reason, isGerardoExit }));
      }
    } catch { /* ignore */ }
  }, [cart, reason, isGerardoExit]);

  useEffect(() => {
    if (!pendingApproval) return;
    const isGerardoPending = pendingApproval.startsWith && pendingApproval.startsWith('gerardo:');
    const interval = setInterval(async () => {
      try {
        if (isGerardoPending) {
          // Polling sobre la salida directa (no hay approval_request_id aún)
          const exitId = pendingApproval.split(':', 2)[1];
          const { data } = await axios.get(`${API}/exits/${exitId}`, { withCredentials: true });
          if (data.status === 'approved') {
            toast.success('✅ Salida aprobada');
            setPendingApproval(null);
            fetchHistory(); refreshProducts();
          } else if (data.status === 'rejected') {
            toast.error('❌ Salida rechazada · stock devuelto');
            setPendingApproval(null);
            fetchHistory(); refreshProducts();
          } else if (data.status === 'pending' && data.approval_request_id) {
            // Gerardo confirmó → pasa al flujo normal
            toast.info('✅ Gerardo confirmó · esperando aprobación del admin…');
            setPendingApproval(data.approval_request_id);
          }
        } else {
          const { data } = await axios.get(`${API}/approvals/${pendingApproval}`, { withCredentials: true });
          if (data.status === 'approved') {
            toast.success('✅ Salida aprobada');
            setPendingApproval(null);
            fetchHistory(); refreshProducts();
          } else if (data.status === 'rejected') {
            toast.error('❌ Salida rechazada · stock devuelto');
            setPendingApproval(null);
            fetchHistory(); refreshProducts();
          }
        }
      } catch (e) { /* ignore */ }
    }, 2500);
    return () => clearInterval(interval);
  }, [pendingApproval]);

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/exits`, { withCredentials: true });
      setHistory(data);
    } catch (e) { /* ignore */ }
  };

  const hiddenParentIds = useMemo(() => {
    const parentIds = new Set(products.map(p => p.parent_product_id).filter(Boolean));
    return new Set(
      products
        .filter(p => (p.has_variants || parentIds.has(p.id)) && p.visible_in_pos === false)
        .map(p => p.id)
    );
  }, [products]);

  const filtered = products.filter((p) => {
    if (p.has_variants) return false;
    if (p.visible_in_pos === false) return false; // Ocultos del sistema
    if (p.parent_product_id && hiddenParentIds.has(p.parent_product_id)) return false; // variante de padre oculto
    const term = searchTerm.toLowerCase();
    return (
      p.name.toLowerCase().includes(term) ||
      (p.sku || '').toLowerCase().includes(term)
    );
  }).slice(0, 100);

  const addToCart = (product) => {
    if (!allowNegative && (product.stock || 0) <= 0) { toast.error('Sin stock'); return; }
    const existing = cart.find((c) => c.product_id === product.id);
    if (existing) {
      if (!allowNegative && existing.quantity + 1 > product.stock) { toast.error(`Máx: ${product.stock}`); return; }
      setCart(cart.map((c) => c.product_id === product.id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.variant_name ? `${product.name} (${product.variant_name})` : product.name,
        sku: product.sku,
        image_url: product.image_url || null,
        max_stock: product.stock || 0,
        quantity: 1
      }]);
    }
  };

  const updateQty = (idx, qty) => {
    // Permitir campo vacío mientras el usuario escribe
    if (qty === '' || qty === null || qty === undefined) {
      setCart(cart.map((c, i) => i === idx ? { ...c, quantity: '' } : c));
      return;
    }
    const num = parseInt(qty);
    if (isNaN(num) || num < 0) return;
    if (num === 0) return removeFromCart(idx);
    const item = cart[idx];
    if (!allowNegative && num > item.max_stock) { toast.error(`Máx: ${item.max_stock}`); return; }
    setCart(cart.map((c, i) => i === idx ? { ...c, quantity: num } : c));
  };

  const commitQty = (idx) => {
    // Al perder foco, si el campo quedó vacío o inválido, forzar 1
    const item = cart[idx];
    if (!item) return;
    const num = parseInt(item.quantity);
    if (isNaN(num) || num < 1) {
      setCart(cart.map((c, i) => i === idx ? { ...c, quantity: 1 } : c));
    }
  };

  const removeFromCart = (idx) => setCart(cart.filter((_, i) => i !== idx));
  const totalQty = cart.reduce((s, c) => s + (parseInt(c.quantity) || 0), 0);

  const handleSubmit = async () => {
    if (cart.length === 0) { toast.error('Agrega al menos un producto'); return; }
    if (!reason.trim()) { toast.error('Indica el motivo'); return; }
    setSubmitting(true);
    try {
      const payload = {
        items: cart.map((c) => ({ product_id: c.product_id, product_name: c.product_name, quantity: parseInt(c.quantity) })),
        reason: reason.trim(),
        is_gerardo_exit: isGerardoExit
      };
      const { data } = await axios.post(`${API}/exits`, payload, { withCredentials: true });
      if (data.status === 'approved') {
        toast.success(`Salida ${data.folio} registrada`);
      } else if (data.status === 'pending_gerardo') {
        toast.info(`Salida ${data.folio} · esperando que Gerardo confirme…`);
        setPendingApproval(`gerardo:${data.id}`);
      } else {
        toast.info(`Salida ${data.folio} · esperando aprobación…`);
        setPendingApproval(data.approval_request_id);
      }
      setCart([]); setReason(''); setIsGerardoExit(false);
      refreshProducts(); fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally { setSubmitting(false); }
  };

  const statusBadge = (status) => {
    if (status === 'approved') return <span className="px-2 py-0.5 text-[10px] rounded bg-emerald-100 text-emerald-700 border border-emerald-300">Aprobado</span>;
    if (status === 'rejected') return <span className="px-2 py-0.5 text-[10px] rounded bg-rose-100 text-rose-700 border border-rose-300">Rechazado</span>;
    return <span className="px-2 py-0.5 text-[10px] rounded bg-amber-100 text-amber-700 border border-amber-300 animate-pulse">Pendiente</span>;
  };

  const filteredHistory = useMemo(() => {
    return history.filter((h) => {
      if (hStatus !== 'all' && h.status !== hStatus) return false;
      if (hFrom && h.created_at < hFrom) return false;
      if (hTo && h.created_at > hTo + 'T23:59:59') return false;
      if (hSearch) {
        const t = hSearch.toLowerCase();
        const inFolio = (h.folio || '').toLowerCase().includes(t);
        const inReason = (h.reason || '').toLowerCase().includes(t);
        const inUser = (h.created_by_name || '').toLowerCase().includes(t);
        const inItems = (h.items || []).some((it) => (it.product_name || '').toLowerCase().includes(t));
        if (!(inFolio || inReason || inUser || inItems)) return false;
      }
      return true;
    });
  }, [history, hSearch, hFrom, hTo, hStatus]);

  return (
    <MainLayout>
      <div className="p-8">
        <div className="flex items-center gap-3 mb-6">
          <ArrowSquareOut size={32} weight="bold" className="text-rose-600" />
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Salidas</h1>
            <p className="text-muted-foreground">
              Registra mermas, regalos y otras salidas. {!isAdmin && 'Requieren aprobación del admin por Telegram.'}
            </p>
          </div>
        </div>

        {pendingApproval && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded text-amber-900 text-sm animate-pulse">
            {String(pendingApproval).startsWith('gerardo:')
              ? '⏳ Esperando confirmación de Gerardo…'
              : '⏳ Esperando aprobación del admin…'}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 bg-white border border-border rounded-sm p-3 flex flex-col">
            <div className="relative mb-2">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar…" className="pl-9 h-9 text-sm" data-testid="exit-search"
              />
            </div>
            <div className="flex-1 overflow-auto space-y-1 max-h-[60vh]">
              {filtered.map((p) => (
                <button
                  key={p.id} onClick={() => addToCart(p)} disabled={!allowNegative && (p.stock || 0) <= 0}
                  className="w-full flex items-center gap-2 p-2 border border-border rounded hover:bg-rose-50 hover:border-rose-400 btn-transition text-left disabled:opacity-40"
                  data-testid={`exit-add-${p.id}`}
                >
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-10 h-10 object-cover rounded flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 bg-slate-100 rounded flex-shrink-0 flex items-center justify-center">
                      <ImageIcon size={18} className="text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.name}{p.variant_name ? ` (${p.variant_name})` : ''}</p>
                    <p className="text-[10px] text-muted-foreground">SKU: {p.sku} · Stock: <span className={(p.stock || 0) <= 0 ? 'text-rose-500 font-semibold' : 'text-emerald-600 font-semibold'}>{p.stock || 0}</span></p>
                  </div>
                </button>
              ))}
              {filtered.length === 0 && <p className="text-center text-muted-foreground py-6 text-sm">Sin productos</p>}
            </div>
          </div>

          <div className="lg:col-span-7 bg-white border border-border rounded-sm p-4 flex flex-col">
            <h2 className="text-xl font-semibold mb-3">Items a sacar</h2>
            <div className="flex-1 overflow-auto space-y-2 mb-4 max-h-[55vh]">
              {cart.map((c, i) => (
                <div key={i} className="border border-border rounded p-2 flex items-start gap-3" data-testid={`exit-cart-item-${i}`}>
                  {c.image_url ? (
                    <img src={c.image_url} alt={c.product_name} className="w-14 h-14 object-cover rounded flex-shrink-0" />
                  ) : (
                    <div className="w-14 h-14 bg-slate-100 rounded flex-shrink-0 flex items-center justify-center">
                      <ImageIcon size={22} className="text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.product_name}</p>
                    <p className="text-xs text-muted-foreground">SKU: {c.sku} · {c.max_stock} → <span className="text-rose-600 font-semibold">{c.max_stock - c.quantity}</span></p>
                    <div className="flex items-center gap-2 mt-1">
                      <Label className="text-xs">Cant:</Label>
                      <Input type="number" min="1" max={c.max_stock} value={c.quantity}
                        onChange={(e) => updateQty(i, e.target.value)}
                        onBlur={() => commitQty(i)}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-24 h-8" data-testid={`exit-qty-${i}`} />
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeFromCart(i)} data-testid={`exit-remove-${i}`}><Trash size={14} /></Button>
                </div>
              ))}
              {cart.length === 0 && <p className="text-muted-foreground text-center py-8">Sin items</p>}
            </div>

            <div className="border-t pt-3 space-y-2">
              <div className={`flex items-center justify-between p-3 rounded border ${isGerardoExit ? 'bg-amber-50 border-amber-400' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-center gap-2">
                  <Truck size={20} weight={isGerardoExit ? 'fill' : 'regular'} className={isGerardoExit ? 'text-amber-700' : 'text-slate-500'} />
                  <div>
                    <Label htmlFor="is-gerardo" className="cursor-pointer font-semibold">Salida Gerardo</Label>
                    <p className="text-[11px] text-muted-foreground leading-tight">
                      {isGerardoExit
                        ? 'Gerardo confirmará primero. Después Rozette aprueba.'
                        : 'Activa si la mercancía la lleva Gerardo Rosette.'}
                    </p>
                  </div>
                </div>
                <Switch id="is-gerardo" checked={isGerardoExit} onCheckedChange={setIsGerardoExit} data-testid="is-gerardo-switch" />
              </div>
              <div>
                <Label htmlFor="reason">Motivo *</Label>
                <Textarea id="reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Ej: merma, regalo, error, uso interno…" data-testid="exit-reason" />
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-sm text-muted-foreground">Total piezas:</span>
                <span className="text-2xl font-bold text-rose-600">{totalQty}</span>
              </div>
              <Button onClick={handleSubmit} disabled={submitting || cart.length === 0 || !!pendingApproval}
                className={`w-full ${isGerardoExit ? 'bg-amber-600 hover:bg-amber-700' : 'bg-rose-600 hover:bg-rose-700'}`} data-testid="exit-submit">
                {submitting
                  ? 'Procesando…'
                  : isGerardoExit
                    ? 'Enviar a Gerardo'
                    : (isAdmin ? 'Registrar salida' : 'Solicitar aprobación')}
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white border border-border rounded-sm">
          <h2 className="text-lg font-semibold p-4 border-b flex items-center gap-2">
            <Receipt size={20} />
            Historial de salidas <span className="text-xs font-normal text-muted-foreground">({filteredHistory.length} de {history.length})</span>
          </h2>
          <div className="p-3">
            <HistoryFilters
              searchTerm={hSearch} onSearch={setHSearch}
              dateFrom={hFrom} onDateFrom={setHFrom}
              dateTo={hTo} onDateTo={setHTo}
              status={hStatus} onStatus={setHStatus}
              statusOptions={[
                { value: 'pending', label: 'Pendientes' },
                { value: 'approved', label: 'Aprobadas' },
                { value: 'rejected', label: 'Rechazadas' }
              ]}
            />
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Folio</th>
                <th className="px-3 py-2 text-left">Fecha</th>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2 text-left">Por</th>
                <th className="px-3 py-2 text-left">Motivo</th>
                <th className="px-3 py-2 text-right">Piezas</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredHistory.map((h) => (
                <tr key={h.id} onDoubleClick={() => setDetail({ open: true, record: h })}
                    className="hover:bg-slate-50 cursor-pointer" title="Doble clic para ver detalle">
                  <td className="px-3 py-2 font-mono text-xs">{h.folio}</td>
                  <td className="px-3 py-2 text-xs">{new Date(h.created_at).toLocaleString('es-MX')}</td>
                  <td className="px-3 py-2">{statusBadge(h.status)}</td>
                  <td className="px-3 py-2 text-xs">{h.created_by_name || '-'}</td>
                  <td className="px-3 py-2 text-xs max-w-[240px] truncate" title={h.reason}>{h.reason}</td>
                  <td className="px-3 py-2 text-right font-semibold">{h.total_qty}</td>
                </tr>
              ))}
              {filteredHistory.length === 0 && (
                <tr><td colSpan="6" className="px-3 py-8 text-center text-muted-foreground">Sin resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <HistoryDetailDialog
        open={detail.open}
        onOpenChange={(o) => setDetail({ open: o, record: o ? detail.record : null })}
        kind="exit"
        record={detail.record}
      />
    </MainLayout>
  );
};
